import { CUSTOMER_LEVEL, ISuccessApiResponse } from '@wyz/types';
import { callApi, useOnSuccessQuery, useQuery } from '@wyz/utils';
import { useEffect } from 'react';
import { CommonQueryKeys } from '~/common/api';
import { useApplicationContext, useConfig } from '~/common/context';
import { applyTheme, chatDisplay, initOmniCanal } from '~/common/helpers';
import { usePurchaseContext } from '~/purchase/context';

/**
 * Side effects driven by the selected primary customer: chat widget, theme and
 * usetiful tags. Shared by every primary customer selector implementation.
 */
export function useCustomerSideEffects() {
  const { state } = usePurchaseContext();
  const { application, auth } = useApplicationContext();
  const config = useConfig();
  const call = callApi({});

  const chatTokenQuery = useQuery({
    queryKey: CommonQueryKeys.appContext('chat' + auth.user?.profile.sub),
    enabled:
      state.primaryCustomer?.level === CUSTOMER_LEVEL.N1 &&
      application.features.enabledChat &&
      Boolean(state.primaryCustomer.deliveryInformation?.country?.chatId),
    refetchOnMount: true,
    queryFn: () =>
      call<ISuccessApiResponse<string>>(
        `${config.apiUrl}/tenants/api/wtb/tenants/${config.tenantCode}/customers/${state.primaryCustomer?.code}/chat/token`,
      ),
  });

  useOnSuccessQuery(chatTokenQuery, (chatToken) => {
    const chatId = state.primaryCustomer?.deliveryInformation?.country?.chatId;
    if (application?.features.enabledChat && chatToken.data && chatId) {
      initOmniCanal(
        chatId,
        chatToken.data,
        application?.userTheme?.primaryColor || undefined,
      );
    }
  });

  useEffect(() => {
    if (state.primaryCustomer) {
      applyTheme(state.primaryCustomer.matchedTheme ?? application.userTheme);
    }
  }, [application.userTheme, state.primaryCustomer]);

  useEffect(() => {
    if (state.primaryCustomer?.level) {
      // @ts-expect-error required by usetiful
      window.usetifulTags = {
        // @ts-expect-error required by usetiful
        ...window.usetifulTags,
        customerType: state.primaryCustomer.level,
        mode:
          state.primaryCustomer.level === CUSTOMER_LEVEL.N1
            ? 'DISTRIBUTION'
            : 'REDISTRIBUTION',
      };
    }
  }, [state.primaryCustomer?.level]);

  useEffect(() => {
    if (application.features.enabledChat && state.primaryCustomer?.level) {
      chatDisplay(state.primaryCustomer?.level === CUSTOMER_LEVEL.N1);
    }
  }, [state.primaryCustomer?.level, application.features.enabledChat]);
}
